import { useUser } from '@/app/providers';
import { FloatingDeleteButton } from '@/components';
import { useRemoveDeviceAssignment } from '@/features/device-assignment/hooks';
import { CompanyDevicesSection } from '@/features/devices/components/CompanyDevicesSection';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface DevicesTabProps {
  companyId: string;
  // Lets the company view drill into a daughter company that owns one of the listed devices.
  onCompanyClick?: (company: { id: string; name: string }) => void;
}

/**
 * Devices tab of the company view. It lists every device of the company, including the
 * ones already assigned to a daughter company, and lets the user take them back.
 */
export function DevicesTab({ companyId, onCompanyClick }: DevicesTabProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const removeAssignment = useRemoveDeviceAssignment();

  const ownCompanyId = user?.companyUuid ?? '';
  // Taking devices back only makes sense for a company the user does not act for.
  const canRemove = Boolean(ownCompanyId) && ownCompanyId !== companyId;

  return (
    <CompanyDevicesSection
      companyId={companyId}
      onCompanyClick={onCompanyClick}
      renderSelectionActions={({ selectedIds, clearSelection }) => (
        <FloatingDeleteButton
          labelKey="devices.remove.action"
          disabled={!canRemove}
          isDeleting={removeAssignment.isPending}
          modalTitle={t('devices.remove.title')}
          modalMessage={t('devices.remove.message', { count: selectedIds.size })}
          onDelete={async () => {
            try {
              await removeAssignment.mutateAsync({
                deviceIds: Array.from(selectedIds),
                ownCompanyId,
                companyId,
              });
              clearSelection();
              toast.success(t('devices.remove.success'));
            } catch {
              toast.error(t('devices.remove.error'));
            }
          }}
        />
      )}
    />
  );
}
